/**
 * Post-login return URL helpers.
 *
 * Reuses the existing `oauth_redirect_url` sessionStorage key (already written before
 * OAuth IdP redirects) so password/OTP/passkey/TOTP and OAuth share one return path.
 */

/** Existing OAuth callback key — do not rename; OAuthCallback already reads it. */
export const LOGIN_RETURN_URL_STORAGE_KEY = 'oauth_redirect_url';

/**
 * Same-origin relative path only (open-redirect safe).
 * Rejects protocol-relative URLs, absolute URLs, and `/login` itself (loop risk).
 */
export function isSafeLoginReturnPath(path: string): boolean {
  const trimmed = path.trim();
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) return false;
  if (trimmed.includes('://')) return false;
  const pathOnly = trimmed.split(/[?#]/, 1)[0] || '';
  if (pathOnly === '/login') return false;
  return true;
}

/** Decode + validate a raw redirect query/storage value. */
export function normalizeLoginReturnPath(raw: string | null | undefined): string | null {
  if (raw == null) return null;
  let value = raw.trim();
  if (!value) return null;
  try {
    value = decodeURIComponent(value);
  } catch {
    // keep undecoded value
  }
  value = value.trim();
  return isSafeLoginReturnPath(value) ? value : null;
}

export function persistLoginReturnUrl(path: string): void {
  const safe = normalizeLoginReturnPath(path);
  if (safe) {
    sessionStorage.setItem(LOGIN_RETURN_URL_STORAGE_KEY, safe);
  }
}

export function peekLoginReturnUrl(): string | null {
  return normalizeLoginReturnPath(sessionStorage.getItem(LOGIN_RETURN_URL_STORAGE_KEY));
}

/** Read and clear the stored return URL. */
export function consumeLoginReturnUrl(): string | null {
  const value = peekLoginReturnUrl();
  sessionStorage.removeItem(LOGIN_RETURN_URL_STORAGE_KEY);
  return value;
}

export function clearLoginReturnUrl(): void {
  sessionStorage.removeItem(LOGIN_RETURN_URL_STORAGE_KEY);
}

/**
 * Decide what to store before leaving for an OAuth IdP.
 * Prefers an already-persisted return URL (from `?redirect=`), else a safe current path
 * (e.g. `/file/:id`), and never stores bare `/login`.
 */
export function resolveOAuthReturnUrlToPersist(currentPathWithSearch: string): string | null {
  const existing = peekLoginReturnUrl();
  if (existing) return existing;

  try {
    const url = new URL(currentPathWithSearch, 'http://local.invalid');
    const fromQuery = normalizeLoginReturnPath(url.searchParams.get('redirect'));
    if (fromQuery) return fromQuery;
  } catch {
    // fall through
  }

  return normalizeLoginReturnPath(currentPathWithSearch);
}
